import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, CalendarCheck, Stethoscope, Activity, PieChart, Clock, TrendingUp, LayoutList } from 'lucide-react';
import { dashboardAPI } from '../../../services/dashboardService';
import StatCard from './ui/StatCard';
import SectionCard from './ui/SectionCard';
import ProgressBar from './ui/ProgressBar';
import DataTable from './ui/DataTable';
import CustomAreaChart from '../../../components/Charts/CustomAreaChart';
import CustomDonutChart from '../../../components/Charts/CustomDonutChart';

const STATUS_CATEGORIES = [
    { value: 'pending', color: 'bg-amber-500' },
    { value: 'confirmed', color: 'bg-blue-500' },
    { value: 'completed', color: 'bg-emerald-500' },
    { value: 'cancelled', color: 'bg-rose-500' }
];

const STATUS_BADGE_COLORS = {
    pending: 'yellow',
    confirmed: 'blue',
    completed: 'green',
    cancelled: 'red'
};

const STATUS_LABEL_KEYS = ['pending', 'confirmed', 'completed', 'cancelled', 'no_show'];

const MedicalTab = ({ filters, onViewDetailed }) => {
    const { t } = useTranslation();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const getStatusLabel = useCallback((status) => {
        const key = status?.toLowerCase();
        return STATUS_LABEL_KEYS.includes(key)
            ? t(`systemDashboard.medical.statusLabels.${key}`)
            : status || t('systemDashboard.medical.unknown');
    }, [t]);

    const APPOINTMENT_COLUMNS = [
        { key: 'petName', header: t('systemDashboard.medical.columns.pet') },
        { key: 'ownerName', header: t('systemDashboard.medical.columns.owner') },
        { key: 'clinicName', header: t('systemDashboard.medical.columns.clinic') },
        {
            key: 'date',
            header: t('systemDashboard.medical.columns.date'),
            width: '140px',
            render: (row) => (row.date ? new Date(row.date).toLocaleDateString() : t('systemDashboard.common.na'))
        },
        {
            key: 'status',
            header: t('systemDashboard.medical.columns.status'),
            width: '120px',
            render: (row) => {
                const color = STATUS_BADGE_COLORS[row.status?.toLowerCase()] || 'gray';
                return (
                    <span className={`status-badge ${color}`}>
                        {getStatusLabel(row.status)}
                    </span>
                );
            }
        }
    ];

    const loadMedicalStats = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await dashboardAPI.getMedicalStats(filters);
            setStats(response.data);
        } catch (err) {
            console.error('Failed to load medical stats:', err);
            setError(err.message || t('systemDashboard.medical.loadError'));
        } finally {
            setLoading(false);
        }
    }, [filters, t]);

    useEffect(() => {
        loadMedicalStats();
    }, [loadMedicalStats]);

    const statusData = useMemo(() => {
        if (!stats?.appointmentsByStatus) return [];
        return Object.entries(stats.appointmentsByStatus).map(([key, value]) => ({
            name: getStatusLabel(key),
            value
        }));
    }, [stats?.appointmentsByStatus, getStatusLabel]);

    const graphData = stats?.graph_data || [];

    if (error) {
        return (
            <div className="error-container">
                <p>{error}</p>
                <button onClick={loadMedicalStats} className="retry-button">{t('systemDashboard.common.retry')}</button>
            </div>
        );
    }

    return (
        <div className="sd-container">
            <div className="sd-detailed-link-row">
                <button
                    onClick={onViewDetailed}
                    className="sd-detailed-link"
                >
                    {t('systemDashboard.common.detailed')} <ArrowRight size={14} />
                </button>
            </div>

            {/* KPI Cards */}
            <div className="sd-grid sd-grid--kpi-primary">
                <StatCard
                    icon={CalendarCheck}
                    label={t('systemDashboard.medical.appointments')}
                    value={stats?.totalAppointments || 0}
                    trend={stats?.appointmentGrowth}
                    trendLabel=""
                    color="bg-rose-500"
                    loading={loading}
                />
                <StatCard
                    icon={Stethoscope}
                    label={t('systemDashboard.medical.clinics')}
                    value={stats?.activeClinics || 0}
                    subtitle={t('systemDashboard.medical.registered')}
                    color="bg-teal-500"
                    loading={loading}
                />
                <StatCard
                    icon={Activity}
                    label={t('systemDashboard.medical.completion')}
                    value={`${(stats?.completionRate || 0).toFixed(1)}%`}
                    subtitle={t('systemDashboard.medical.statusLabels.completed')}
                    color="bg-amber-500"
                    loading={loading}
                />
                <StatCard
                    icon={Clock}
                    label={t('systemDashboard.medical.outpatient')}
                    value={stats?.outpatientVisits || 0}
                    color="bg-blue-500"
                    loading={loading}
                />
            </div>

            {/* Charts */}
            {!loading && (
                <div className="sd-grid sd-grid--charts">
                    <SectionCard title={t('systemDashboard.medical.appointmentStatus')} icon={PieChart}>
                        <CustomDonutChart
                            data={statusData}
                            totalValue={stats?.totalAppointments || 0}
                            title=""
                        />
                    </SectionCard>
                    <SectionCard title={t('systemDashboard.medical.appointmentsOverTime')} icon={TrendingUp} className="sd-card--wide">
                        <CustomAreaChart data={graphData} color="#ef4444" title="" />
                    </SectionCard>
                </div>
            )}

            {/* Status Breakdown + Visit Types */}
            {!loading && (
                <div className="sd-grid sd-grid--split">
                    <SectionCard title={t('systemDashboard.medical.statusBreakdown')} icon={LayoutList}>
                        <div className="sd-progress-bar-list">
                            {stats?.appointmentsByStatus && Object.entries(stats.appointmentsByStatus).map(([status, count]) => {
                                const category = STATUS_CATEGORIES.find((c) => c.value === status.toLowerCase());
                                return (
                                    <ProgressBar
                                        key={status}
                                        label={getStatusLabel(status)}
                                        count={count}
                                        total={stats?.totalAppointments || 1}
                                        color={category?.color}
                                    />
                                );
                            })}
                        </div>
                    </SectionCard>

                    <SectionCard title={t('systemDashboard.medical.visitTypes')} icon={Clock}>
                        <div>
                            <div className="sd-visit-card sd-visit-card--outpatient">
                                <div>
                                    <p className="sd-visit-card__title">{t('systemDashboard.medical.outpatientVisits')}</p>
                                    <p className="sd-visit-card__subtitle">{t('systemDashboard.medical.regularConsultations')}</p>
                                </div>
                                <p className="sd-visit-card__value sd-visit-card__value--outpatient">
                                    {(stats?.outpatientVisits || 0).toLocaleString()}
                                </p>
                            </div>
                            <div className="sd-visit-card sd-visit-card--inpatient">
                                <div>
                                    <p className="sd-visit-card__title">{t('systemDashboard.medical.inpatientVisits')}</p>
                                    <p className="sd-visit-card__subtitle">{t('systemDashboard.medical.hospitalStays')}</p>
                                </div>
                                <p className="sd-visit-card__value sd-visit-card__value--inpatient">
                                    {(stats?.inpatientVisits || 0).toLocaleString()}
                                </p>
                            </div>
                        </div>
                    </SectionCard>
                </div>
            )}

            {/* Recent Appointments */}
            {!loading && (
                <SectionCard title={t('systemDashboard.medical.recentAppointments')} icon={CalendarCheck}>
                    <DataTable
                        columns={APPOINTMENT_COLUMNS}
                        data={stats?.recentAppointments || []}
                        emptyText={t('systemDashboard.medical.noRecentAppointments')}
                    />
                </SectionCard>
            )}
        </div>
    );
};

export default MedicalTab;
